import Keycloak from 'keycloak-js'

import {
  AUTH_CLIENT_ID,
  AUTH_REALM,
  AUTH_SCOPE,
  AUTH_URL,
  PRIORITIZED_USER_ROLES,
  type UserRolesEnum,
} from '../../config'
import { getRedirectUri } from '../../lib/helpers/get-redirect-uri'
import { initAuthSync, notifyLogout } from './auth-sync'

export type KeycloakHookCallbacks = {
  onActionUpdate: Keycloak['onActionUpdate']
  onAuthError: Keycloak['onAuthError']
  onAuthLogout: Keycloak['onAuthLogout']
  onAuthRefreshError: Keycloak['onAuthRefreshError']
  onAuthRefreshSuccess: Keycloak['onAuthRefreshSuccess']
  onAuthSuccess: Keycloak['onAuthSuccess']
  onReady: Keycloak['onReady']
  onTokenExpired: Keycloak['onTokenExpired']
}
interface AuthManager {
  initKeycloak: (hookCallbacks: KeycloakHookCallbacks) => Promise<boolean>
  login: (
    ...args: Parameters<Keycloak['login']>
  ) => ReturnType<Keycloak['login']>
  logout: (
    ...args: Parameters<Keycloak['logout']>
  ) => ReturnType<Keycloak['logout']>
  isAuthenticated: () => Promise<boolean>
  getToken: () => Keycloak['token']
  getTokenParsed: () => Keycloak['tokenParsed']
  getTimeSkew: () => Keycloak['timeSkew']
  refreshToken: (successCallback?: () => void) => Promise<string | undefined>
  getUsername: () => string | undefined
  getRole: () => UserRolesEnum | undefined
  getIsAuthenticated: () => boolean | undefined
  MIN_TOKEN_VALIDITY_TIME_IN_SECONDS: number
}

const MIN_TOKEN_VALIDITY_TIME_IN_SECONDS = 30

const _kc = new Keycloak({
  url: AUTH_URL,
  realm: AUTH_REALM,
  clientId: AUTH_CLIENT_ID,
})

const getIsAuthenticated = () => _kc.authenticated

let initPromise: Promise<boolean> | null = null

const initKeycloak = (hookCallbacks: KeycloakHookCallbacks) => {
  if (initPromise) {
    return initPromise
  }

  _kc.onActionUpdate = (params) => {
    hookCallbacks.onActionUpdate?.(params)
  }
  _kc.onAuthError = (params) => {
    hookCallbacks.onAuthError?.(params)
  }
  _kc.onAuthLogout = () => {
    hookCallbacks.onAuthLogout?.()
    notifyLogout()
  }
  _kc.onAuthRefreshError = () => {
    hookCallbacks.onAuthRefreshError?.()
  }
  _kc.onAuthRefreshSuccess = () => {
    hookCallbacks.onAuthRefreshSuccess?.()
  }
  _kc.onAuthSuccess = () => {
    hookCallbacks.onAuthSuccess?.()
  }
  _kc.onReady = (params) => {
    hookCallbacks.onReady?.(params)
  }
  _kc.onTokenExpired = () => {
    hookCallbacks.onTokenExpired?.()
  }

  const redirectUri = getRedirectUri()

  initAuthSync(() => {
    void _kc.logout({
      redirectUri: getRedirectUri(),
    })
  })

  initAuthSync(() => {
    void _kc.logout({
      redirectUri: getRedirectUri(),
    })
  })

  initPromise = _kc.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    redirectUri: getRedirectUri(),
    scope: AUTH_SCOPE,
  })

  initPromise.catch((error) => {
    initPromise = null
    console.error(error)
  })

  return initPromise
}

const login = (...args: Parameters<typeof _kc.login>) =>
  _kc.login({
    redirectUri: getRedirectUri(),
    ...args[0],
  })

const logout = (...args: Parameters<typeof _kc.logout>) => {
  notifyLogout()
  return _kc.logout({
    redirectUri: getRedirectUri(),
    ...args[0],
  })
}

const getToken = () => _kc.token
const getTokenParsed = () => _kc.tokenParsed

const getTimeSkew = () => _kc.timeSkew

const isAuthenticated = async () => {
  try {
    const valid = await _kc.updateToken(MIN_TOKEN_VALIDITY_TIME_IN_SECONDS)
    return !!_kc.token && valid
  } catch {
    return false
  }
}

const refreshToken = async (successCallback?: () => void) => {
  try {
    const refreshed = await _kc.updateToken(MIN_TOKEN_VALIDITY_TIME_IN_SECONDS)
    if (refreshed) {
      successCallback?.()
    }
    return _kc.token
  } catch (err) {
    console.error('Failed to refresh token', err)
    await logout()
  }
}

const getUsername = () => _kc.tokenParsed?.preferred_username

const getRole = () => {
  const tokenParsed = _kc.tokenParsed
  if (!tokenParsed) return undefined
  const roles = tokenParsed.roles
  const role = PRIORITIZED_USER_ROLES.find((role) => roles.includes(role))
  return role
}

const AuthManager: AuthManager = {
  initKeycloak,
  login,
  logout,
  isAuthenticated,
  getToken,
  getTokenParsed,
  getTimeSkew,
  refreshToken,
  getUsername,
  getRole,
  getIsAuthenticated,
  MIN_TOKEN_VALIDITY_TIME_IN_SECONDS,
}

export default AuthManager
